import { supabase } from './supabase';

export type OnlineBookingVolumePeriod = 'this_month' | 'last_30_days';

export type DateRangeInput = { start: Date; end: Date };

export type OnlineBookingFunnelStep = {
  step: string;
  count: number;
  icon: string;
};

export type OnlineBookingVolumeData = {
  value: string;
  label: string;
  funnelData: OnlineBookingFunnelStep[];
  bannerSubmits: number;
  showBannerSubmits: boolean;
  totalStarts: number;
  totalCompletions: number;
};

export type OnlineBookingMonthYtdRow = {
  monthKey: string;
  label: string;
  conversionRate: number;
  totalStarts: number;
  totalCompletions: number;
  isCurrentMonth: boolean;
};

const EMPTY_FUNNEL: OnlineBookingFunnelStep[] = [
  { step: 'Location', count: 0, icon: 'MapPin' },
  { step: 'Category', count: 0, icon: 'Tag' },
  { step: 'Service', count: 0, icon: 'Wrench' },
  { step: 'Date & Time', count: 0, icon: 'Calendar' },
  { step: 'Customer Info', count: 0, icon: 'User' },
  { step: 'Completed', count: 0, icon: 'CheckCircle' },
];

export function emptyOnlineBookingVolumeData(): OnlineBookingVolumeData {
  return {
    value: '0.0%',
    label: 'Conversion Rate (0/0)',
    funnelData: EMPTY_FUNNEL.map((s) => ({ ...s })),
    bannerSubmits: 0,
    showBannerSubmits: false,
    totalStarts: 0,
    totalCompletions: 0,
  };
}

export function getOnlineBookingVolumeRange(period: OnlineBookingVolumePeriod): {
  start: Date;
  end: Date;
  label: string;
} {
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  if (period === 'last_30_days') {
    const start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
    return { start, end, label: 'Last 30 days' };
  }

  const start = new Date(end.getFullYear(), end.getMonth(), 1, 0, 0, 0, 0);
  return { start, end, label: 'This month' };
}

/** Calendar month range (current month ends today). `monthKey` = YYYY-MM. */
export function getMonthDateRange(monthKey: string): DateRangeInput {
  const [year, month] = monthKey.split('-').map(Number);
  const start = new Date(year, month - 1, 1, 0, 0, 0, 0);
  const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);
  const now = new Date();
  const isCurrent =
    now.getFullYear() === year && now.getMonth() === month - 1;
  if (isCurrent) {
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }
  return { start, end: monthEnd };
}

function buildFunnelFromSteps(
  stepData: Array<{ step: string; session_id: string | null }> | null,
): Pick<
  OnlineBookingVolumeData,
  'funnelData' | 'totalStarts' | 'totalCompletions' | 'value' | 'label'
> {
  const stepCounts = {
    location: new Set<string>(),
    category: new Set<string>(),
    service: new Set<string>(),
    datetime: new Set<string>(),
    customer: new Set<string>(),
    completed: new Set<string>(),
  };

  for (const item of stepData || []) {
    const key = item.step as keyof typeof stepCounts;
    if (stepCounts[key] && item.session_id) stepCounts[key].add(String(item.session_id));
  }

  const funnelData: OnlineBookingFunnelStep[] = [
    { step: 'Location', count: stepCounts.location.size, icon: 'MapPin' },
    { step: 'Category', count: stepCounts.category.size, icon: 'Tag' },
    { step: 'Service', count: stepCounts.service.size, icon: 'Wrench' },
    { step: 'Date & Time', count: stepCounts.datetime.size, icon: 'Calendar' },
    { step: 'Customer Info', count: stepCounts.customer.size, icon: 'User' },
    { step: 'Completed', count: stepCounts.completed.size, icon: 'CheckCircle' },
  ];

  const totalStarts = stepCounts.location.size;
  const totalCompletions = stepCounts.completed.size;
  const conversionRate =
    totalStarts > 0 ? ((totalCompletions / totalStarts) * 100).toFixed(1) : '0.0';

  return {
    value: `${conversionRate}%`,
    label: `Conversion Rate (${totalCompletions}/${totalStarts})`,
    funnelData,
    totalStarts,
    totalCompletions,
  };
}

/** Same funnel as KPI Dashboard “Online Booking Volume”. */
export async function fetchOnlineBookingVolumeData(
  businessId: string,
  periodOrRange: OnlineBookingVolumePeriod | DateRangeInput = 'this_month',
): Promise<OnlineBookingVolumeData> {
  const { start, end } =
    typeof periodOrRange === 'string'
      ? getOnlineBookingVolumeRange(periodOrRange)
      : periodOrRange;

  try {
    const { data: defPortal } = await supabase
      .from('online_booking_portals')
      .select('id')
      .eq('business_id', businessId)
      .eq('is_default', true)
      .maybeSingle();

    let isPopupEnabled = false;
    if (defPortal?.id) {
      const { data: popupSettings } = await supabase
        .from('online_booking_settings')
        .select('popup_enabled')
        .eq('portal_id', defPortal.id)
        .maybeSingle();
      isPopupEnabled = !!popupSettings?.popup_enabled;
    }

    const { data: stepData, error } = await supabase
      .from('booking_step_progression')
      .select('step, session_id')
      .eq('business_id', businessId)
      .gte('reached_at', start.toISOString())
      .lte('reached_at', end.toISOString());

    if (error) throw error;

    let bannerSubmits = 0;
    if (isPopupEnabled) {
      const { data: bannerData, error: bannerError } = await supabase
        .from('banner_submissions')
        .select('id')
        .eq('business_id', businessId)
        .gte('submitted_at', start.toISOString())
        .lte('submitted_at', end.toISOString());
      if (!bannerError) bannerSubmits = bannerData?.length || 0;
    }

    const built = buildFunnelFromSteps(stepData);

    return {
      ...built,
      bannerSubmits,
      showBannerSubmits: isPopupEnabled,
    };
  } catch (err) {
    console.error('[fetchOnlineBookingVolumeData]', err);
    return emptyOnlineBookingVolumeData();
  }
}

/** Year-to-date online booking conversion by calendar month (Jan → current). */
export async function fetchOnlineBookingVolumeByMonthYtd(businessId: string): Promise<{
  months: OnlineBookingMonthYtdRow[];
  yearStarts: number;
  yearCompletions: number;
  yearRate: number;
  yearLabel: string;
}> {
  const now = new Date();
  const year = now.getFullYear();
  const yearStart = new Date(year, 0, 1, 0, 0, 0, 0);
  const yearEnd = new Date(now);
  yearEnd.setHours(23, 59, 59, 999);

  const { data: stepData, error } = await supabase
    .from('booking_step_progression')
    .select('step, session_id, reached_at')
    .eq('business_id', businessId)
    .gte('reached_at', yearStart.toISOString())
    .lte('reached_at', yearEnd.toISOString());

  if (error) throw error;

  type MonthBuckets = {
    location: Set<string>;
    completed: Set<string>;
  };
  const byMonth = new Map<string, MonthBuckets>();

  for (const item of stepData || []) {
    if (!item.reached_at || !item.session_id) continue;
    if (item.step !== 'location' && item.step !== 'completed') continue;
    const d = new Date(item.reached_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    let bucket = byMonth.get(key);
    if (!bucket) {
      bucket = { location: new Set(), completed: new Set() };
      byMonth.set(key, bucket);
    }
    if (item.step === 'location') bucket.location.add(String(item.session_id));
    if (item.step === 'completed') bucket.completed.add(String(item.session_id));
  }

  const months: OnlineBookingMonthYtdRow[] = [];
  let yearStarts = 0;
  let yearCompletions = 0;

  for (let m = 0; m <= now.getMonth(); m += 1) {
    const monthKey = `${year}-${String(m + 1).padStart(2, '0')}`;
    const bucket = byMonth.get(monthKey);
    const totalStarts = bucket?.location.size ?? 0;
    const totalCompletions = bucket?.completed.size ?? 0;
    yearStarts += totalStarts;
    yearCompletions += totalCompletions;
    const conversionRate =
      totalStarts > 0 ? (totalCompletions / totalStarts) * 100 : 0;
    months.push({
      monthKey,
      label: new Date(year, m, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      }),
      conversionRate,
      totalStarts,
      totalCompletions,
      isCurrentMonth: m === now.getMonth(),
    });
  }

  return {
    months,
    yearStarts,
    yearCompletions,
    yearRate: yearStarts > 0 ? (yearCompletions / yearStarts) * 100 : 0,
    yearLabel: String(year),
  };
}
